import React from "react"
import { useBlocks } from "app/regions/hooks/use-blocks"
import Stack from "@kit/design/stack"
import Label from "@kit/design/label"
import { createTheme, useInheritedTheme } from "@convertkit/editor"
import SelectMenu from "@kit/design/select-menu"
import { Expander } from "app/regions/blocks/shared/expander"
import PixelSlider from "@kit/design/pixel-slider"
import { ColorInput } from "@kit/design/color-picker"
import Note from "@kit/design/note"
import { FontSettings } from "./font-settings"
import { fontOptions } from "app/regions/blocks/shared/fonts"
import { useSavedColors } from "hooks/use-saved-colors"

const isLimitedFont = fontFamily => {
  const font = fontOptions.find(font => font.value === fontFamily)
  if (font.limited) return true

  return false
}

export function ThemeSettings({
  type,
  section,
  showTextTransform,
  showLinkColor
}) {
  const { update, properties } = useBlocks(section?.block_id)
  const { find } = useInheritedTheme()
  const savedColors = useSavedColors()

  function handleChange(key, value) {
    update({ theme: createTheme(properties.theme, type, key, value) })
  }

  function setLinkColor({ hex }) {
    update({
      theme: createTheme(properties.theme, "link", "color", hex)
    })
  }

  return (
    <Stack spacing={4}>
      <FontSettings
        fontFamily={find(type, "fontFamily")}
        fontWeight={find(type, "fontWeight")}
        onChange={handleChange}
      />
      {isLimitedFont(find(type, "fontFamily")) && (
        <Note
          type="warning"
          title="Limited font support"
          message="This font is not supported on some email clients. We will show an alternative font in these clients."
        />
      )}

      <PixelSlider
        label="Size"
        name="fontSize"
        value={find(type, "fontSize") || 0}
        onChange={value => handleChange("fontSize", Number(value))}
        min={8}
        max={50}
      />

      <Stack spacing={1}>
        <Label>Color</Label>
        <ColorInput
          name="color"
          value={find(type, "color")}
          onChange={color => handleChange("color", color.hex)}
          savedColors={savedColors}
        />
      </Stack>

      <div className="-mb-4">
        <Expander label="Advanced">
          {showLinkColor && (
            <Stack spacing={1}>
              <Label>Link color</Label>
              <ColorInput
                name="linkColor"
                value={find("link", "color")}
                onChange={setLinkColor}
                savedColors={savedColors}
              />
            </Stack>
          )}

          <PixelSlider
            label="Line height"
            name="lineHeight"
            value={find(type, "lineHeight") || 1}
            onChange={value => handleChange("lineHeight", Number(value))}
            min={1}
            max={2}
            step={0.1}
            showUnit={false}
          />

          <PixelSlider
            label="Letter spacing"
            name="letterSpacing"
            value={find(type, "letterSpacing") || 0}
            onChange={value => handleChange("letterSpacing", Number(value))}
            min={-2}
            max={2}
            step={0.1}
            showUnit={false}
          />

          {showTextTransform && (
            <Stack spacing={1}>
              <Label>Capitalization</Label>
              <SelectMenu
                name="textTransform"
                label="textTransform"
                options={[
                  { label: "Normal", value: "none" },
                  { label: "Uppercase", value: "uppercase" }
                ]}
                onChange={e => handleChange("textTransform", e.target.value)}
                value={find(type, "textTransform")}
                className="w-full"
              />
            </Stack>
          )}
        </Expander>
      </div>
    </Stack>
  )
}
